// 토큰 저장 한 곳(계약 SUPPORT 9 화면 약속·RULES SUPPORT 6).
// 지원 탭(= 그 탭 sessionStorage 에 certi.support.session)이면 토큰은 그 탭 sessionStorage 의 certi.support.* 에만 두고
// 일반 로그인 토큰(localStorage)은 읽지도 쓰지도 않는다. 아니면 지금처럼 localStorage 의 앱 키.
// 2차 인증 증표는 지원 탭이면 같은 탭 sessionStorage 의 앱 키(studio 와 같은 방식 — 탭 키를 sessionStorage 로).
// admin-hospital·admin-manufacturer 같은 내용 — 앱마다 다른 것은 APP_NAME·LEGACY_KEYS·PROFILE_KEYS 세 줄.
// Vue·axios 를 들이지 않는 순수 모듈(tests/tokenStore.test.mjs). 토큰·표시용 키 직접 접근은 이 파일 밖에 두지 않는다(verify:rules).
export const APP_NAME = "admin-manufacturer";
export const LEGACY_KEYS = { access: "accessTokenManu", refresh: "refreshTokenManu", mfa: "mfaTokenManu" };
// 다른 회사 화면 이름이 남지 않게 진입·이동 받기 때 지우는 표시용 키(옛 토큰 쿼리 받기와 같은 목록 — 토큰 아님)
export const PROFILE_KEYS = ["nameManu", "managerManu", "companyManu", "companyImageUrlManu", "CompanyInfo", "manufacturerNameManu", "locale"];
export const SUPPORT_KEYS = {
  access: "certi.support.access",
  refresh: "certi.support.refresh",
  session: "certi.support.session",
};

const lazy = (s) => (typeof s === "function" ? s : () => s);
const put = (storage, key, value) => {
  if (value == null) storage.removeItem(key);
  else storage.setItem(key, value);
};

/**
 * 시험용: 저장소를 넣어 만든다(값 또는 함수 — 함수면 부를 때마다 읽음). 화면은 아래 기본 내보내기를 쓴다.
 * embedded(): studio iframe 안이면 true — 그때는 토큰을 studio 가 postMessage 로 주므로 지원 탭 규칙을 건너뛴다.
 */
export function createTokenStore({ local, session, embedded = () => false }) {
  const L = lazy(local);
  const S = lazy(session);
  const getSupportSession = () => {
    if (embedded()) return null;
    try {
      const v = JSON.parse(S().getItem(SUPPORT_KEYS.session) || "null");
      return v && typeof v === "object" ? v : null;
    } catch {
      return null;
    }
  };
  // 끝난 지원 탭도 지원 탭이다(래치 — 일반 로그인 토큰으로 넘어가지 않게). 완전히 지우는 것은 clearSupport() 뿐
  const isSupportTab = () => !!getSupportSession();
  const isSupportEnded = () => getSupportSession()?.ended === true;
  // 지원 탭이면 [sessionStorage, 지원 키], 아니면 [localStorage, 앱 키]
  const slot = (name) =>
    isSupportTab()
      ? [S(), name === "mfa" ? LEGACY_KEYS.mfa : SUPPORT_KEYS[name]]
      : [L(), LEGACY_KEYS[name]];
  const get = (name) => {
    if (isSupportEnded()) return null;
    const [storage, key] = slot(name);
    return storage.getItem(key);
  };
  const set = (name, value) => {
    if (isSupportEnded()) return; // 끝난 탭에는 다시 쓰지 않음(새 진입은 saveSupportExchange)
    const [storage, key] = slot(name);
    put(storage, key, value);
  };
  // 표시용 키(PROFILE_KEYS)도 토큰과 같은 칸 규칙 — 지원 탭이면 그 탭 sessionStorage(다른 탭과 섞이지 않게), 아니면 localStorage
  const profileStorage = () => (isSupportTab() ? S() : L());
  // 완전히 지움(그 뒤 이 탭은 일반 탭) — 일반 로그인이 이어받는 곳(/enter, 옛 토큰 쿼리 받기, 새 지원 교환 직전)만 씀
  const clearSupport = () => {
    const s = S();
    [SUPPORT_KEYS.access, SUPPORT_KEYS.refresh, SUPPORT_KEYS.session, LEGACY_KEYS.mfa, ...PROFILE_KEYS].forEach((k) =>
      s.removeItem(k)
    );
  };
  /**
   * 지원 끝(사유·[종료]·지원 탭 로그아웃): 그 탭 지원 토큰·증표를 지우고 certi.support.session 을 {ended:true, reason} 로 남김.
   * 같은 탭 sessionStorage 라 뒤로 가기·새로 고침에도 이 탭은 끝난 지원 탭(토큰 없음) — 일반 로그인 토큰을 집어 쓰지 않는다.
   */
  const endSupportTab = (reason) => {
    const s = S();
    [SUPPORT_KEYS.access, SUPPORT_KEYS.refresh, LEGACY_KEYS.mfa].forEach((k) => s.removeItem(k));
    s.setItem(SUPPORT_KEYS.session, JSON.stringify({ ended: true, reason: reason || "ENDED" }));
  };
  /**
   * 지원 탭에서 옛 코드가 localStorage 의 앱 토큰·표시용 키(PROFILE_KEYS)를 직접 읽으면 이 탭 값을 돌려줌(11-01).
   * 전자계약 v3 등 계약 팀 보존 검사로 고칠 수 없는 파일이 회사 id·회사 정보를 localStorage 에서 바로 읽어
   * 지원 탭에서 빈 값(또는 같은 브라우저 일반 로그인의 다른 회사 값)을 받던 것. 지원 탭이 아니면 원래 값, 쓰기는 그대로.
   */
  const bridgeLegacyReads = (proto) => {
    const original = proto.getItem;
    const names = Object.fromEntries(Object.entries(LEGACY_KEYS).map(([name, key]) => [key, name]));
    proto.getItem = function getItem(key) {
      if (this === L() && (names[key] || PROFILE_KEYS.includes(key)) && isSupportTab()) {
        return names[key] ? get(names[key]) : S().getItem(key);
      }
      return original.call(this, key);
    };
  };
  return {
    bridgeLegacyReads,
    isSupportTab,
    isSupportEnded,
    getSupportSession,
    getAccessToken: () => get("access"),
    setAccessToken: (v) => set("access", v),
    getRefreshToken: () => get("refresh"),
    setRefreshToken: (v) => set("refresh", v),
    getMfaToken: () => get("mfa"),
    getProfile: (key) => profileStorage().getItem(key),
    setProfile: (key, value) => put(profileStorage(), key, value),
    removeProfile: (key) => profileStorage().removeItem(key),
    // 값이 없으면 지움(다른 계정의 옛 증표가 남지 않게 — 기존 mfa.setMfaToken 과 같음)
    setMfaToken: (v) => set("mfa", v || null),
    clearMfaToken: () => set("mfa", null),
    /** 지원 교환 응답 저장(session 먼저 — 그 순간부터 이 탭은 지원 탭). localStorage 는 건드리지 않음. 모양이 틀리면 false */
    saveSupportExchange(data) {
      if (
        !data ||
        typeof data.accessToken !== "string" ||
        !data.accessToken ||
        typeof data.refreshToken !== "string" ||
        !data.refreshToken ||
        !data.session ||
        typeof data.session !== "object"
      )
        return false;
      const s = S();
      s.removeItem(LEGACY_KEYS.mfa);
      s.setItem(SUPPORT_KEYS.session, JSON.stringify(data.session));
      s.setItem(SUPPORT_KEYS.access, data.accessToken);
      s.setItem(SUPPORT_KEYS.refresh, data.refreshToken);
      return true;
    },
    clearSupport,
    endSupportTab,
    /** 로그아웃: 지원 탭이면 지원 끝(래치, localStorage 는 그대로), 아니면 localStorage 의 토큰·증표 */
    clearTokens() {
      if (isSupportTab()) return endSupportTab("USER");
      const l = L();
      Object.values(LEGACY_KEYS).forEach((k) => l.removeItem(k));
    },
  };
}

const store = createTokenStore({
  local: () => globalThis.localStorage,
  session: () => globalThis.sessionStorage,
  embedded: () => typeof window !== "undefined" && window.parent !== window,
});
// 브라우저에서만(node 시험엔 Storage 없음). 라우터가 이 파일을 먼저 import 하므로, 라우터가 처음 읽힐 때 한 번 읽는 값도 지원 탭 값
if (typeof window !== "undefined" && typeof Storage !== "undefined") store.bridgeLegacyReads(Storage.prototype);

export const {
  isSupportTab,
  isSupportEnded,
  getSupportSession,
  getAccessToken,
  setAccessToken,
  getRefreshToken,
  setRefreshToken,
  getMfaToken,
  setMfaToken,
  getProfile,
  setProfile,
  removeProfile,
  clearMfaToken,
  saveSupportExchange,
  clearSupport,
  endSupportTab,
  clearTokens,
} = store;
